import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import PageShell from '../../../components/layout/PageShell';
import MatchCard from '../../../components/common/cards/MatchCard';
import { getIndividualAssignedMatches } from '../../../services/matchService';
import { useAuth } from '../../../context/AuthContext';

const TABS = [
  { key: 'live', label: 'Live' },
  { key: 'upcoming', label: 'Upcoming' },
  { key: 'past', label: 'Past' },
];

const normalizeTab = (value) => {
  const normalized = String(value || '').toLowerCase().trim();
  return TABS.some((tab) => tab.key === normalized) ? normalized : 'live';
};

const isAbortError = (error) => error?.name === 'AbortError';
const resolveMatchNavigationIds = (match) => {
  const matchId = match?.matchId || match?.id || null;
  const tournamentId = match?.tournamentId || match?.tournament?.id || null;
  return { matchId, tournamentId };
};

const IndividualMyMatchesPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { token } = useAuth();

  const activeTab = useMemo(() => normalizeTab(searchParams.get('tab')), [searchParams]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [matchesByTab, setMatchesByTab] = useState({
    live: [],
    upcoming: [],
    past: [],
  });

  const loadMatches = useCallback(async (signal) => {
    if (!token) return;

    setIsLoading(true);
    setError('');

    try {
      const assignedMatches = await getIndividualAssignedMatches({ token, signal });

      setMatchesByTab({
        live: Array.isArray(assignedMatches?.live) ? assignedMatches.live : [],
        upcoming: Array.isArray(assignedMatches?.upcoming) ? assignedMatches.upcoming : [],
        past: Array.isArray(assignedMatches?.past) ? assignedMatches.past : [],
      });
    } catch (loadError) {
      if (!isAbortError(loadError)) {
        setError('Unable to load your matches right now. Please try again.');
      }
    } finally {
      if (!signal?.aborted) {
        setIsLoading(false);
      }
    }
  }, [token]);

  useEffect(() => {
    const controller = new AbortController();
    loadMatches(controller.signal);
    return () => controller.abort();
  }, [loadMatches]);

  const openMatch = useCallback((match) => {
    const { matchId, tournamentId } = resolveMatchNavigationIds(match);
    if (!matchId || !tournamentId) return;
    navigate(`/public/tournaments/${tournamentId}/matches/${matchId}`);
  }, [navigate]);

  const onTabChange = (tab) => {
    const next = normalizeTab(tab);
    setSearchParams({ tab: next });
  };

  const activeMatches = matchesByTab[activeTab] || [];

  return (
    <PageShell>
      <div className="card border-0 shadow-sm">
        <div className="card-body d-grid gap-3">
          <div className="d-flex align-items-start justify-content-between gap-3 flex-wrap">
            <div>
              <h1 className="h4 fw-bold mb-1">My Matches</h1>
              <p className="text-muted mb-0">Only your playing matches are listed below by status.</p>
            </div>
            <button type="button" className="btn btn-outline-primary btn-sm" onClick={() => loadMatches()} disabled={isLoading}>
              {isLoading ? 'Refreshing…' : 'Refresh'}
            </button>
          </div>

          <div className="btn-group" role="tablist" aria-label="My matches tabs">
            {TABS.map((tab) => (
              <button
                key={tab.key}
                type="button"
                className={`btn ${activeTab === tab.key ? 'btn-primary' : 'btn-outline-primary'}`}
                onClick={() => onTabChange(tab.key)}
              >
                {tab.label} <span className="badge text-bg-light ms-1">{matchesByTab[tab.key]?.length || 0}</span>
              </button>
            ))}
          </div>

          {error ? <div className="alert alert-warning mb-0">{error}</div> : null}
          {isLoading ? <div className="alert alert-info mb-0">Loading your matches…</div> : null}

          {!isLoading && activeMatches.length === 0 ? (
            <div className="alert alert-light border mb-0">No {activeTab} matches found.</div>
          ) : null}

          {!isLoading && activeMatches.length > 0 ? (
            <div className="row g-3">
              {activeMatches.map((match) => {
                const { matchId } = resolveMatchNavigationIds(match);
                return (
                  <div key={matchId || `${match?.title || 'match'}-${match?.startTime || ''}`} className="col-12 col-lg-6">
                    <MatchCard
                      match={match}
                      isLive={activeTab === 'live'}
                      onClick={openMatch}
                      onView={openMatch}
                      showActions
                      compact
                    />
                  </div>
                );
              })}
            </div>
          ) : null}
        </div>
      </div>
    </PageShell>
  );
};

export default IndividualMyMatchesPage;
